import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  ChevronLeft,
  ChevronRight,
  Loader,
  AlertCircle,
  CheckCircle,
  UserCheck,
  UserX,
  ExternalLink,
  Video,
  Users,
  Globe,
  X,
  CalendarDays,
  List,
  Monitor
} from "lucide-react";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, isToday, isSameDay, parseISO, addDays, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import api from "../../utils/api";
import "../../styles/calendar-mobile.css";

const AgentCalendar = () => {
  // State management
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingEventId, setProcessingEventId] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list, calendar, or week
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Default to list view on mobile, calendar on desktop
      if (window.innerWidth < 768 && viewMode === "calendar") {
        setViewMode("list");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [viewMode]);

  // Fetch events for the current week
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 0 });
      const response = await api.get("/classEvents/agent/weekly", {
        params: {
          weekStart: format(weekStart, "yyyy-MM-dd")
        }
      });

      if (response.data.success) {
        setEvents(response.data.data || []);
      } else {
        setError("Failed to load classes");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.response?.data?.message || "Failed to load classes");
    } finally {
      setLoading(false);
    }
  }, [currentWeek]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle registration
  const handleRegister = async (eventId) => {
    setProcessingEventId(eventId);
    try {
      const response = await api.post("/classEvents/agent/register", { eventId });
      
      if (response.data.success) {
        // Update local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event._id === eventId 
              ? { 
                  ...event, 
                  isAgentRegistered: true,
                  agentRegistrationStatus: response.data.requiresExternalRegistration ? "pre-registered" : "registered"
                }
              : event
          )
        );

        // Show external registration link if needed
        if (response.data.requiresExternalRegistration && response.data.externalRegistrationLink) {
          window.open(response.data.externalRegistrationLink, "_blank");
        }
      }
    } catch (err) {
      console.error("Registration error:", err);
      alert(err.response?.data?.message || "Failed to register for class");
    } finally {
      setProcessingEventId(null);
    }
  };

  // Handle unregistration
  const handleUnregister = async (eventId) => {
    if (!window.confirm("Are you sure you want to unregister from this class?")) {
      return;
    }

    setProcessingEventId(eventId);
    try {
      const response = await api.delete("/classEvents/agent/unregister", {
        data: { eventId }
      });
      
      if (response.data.success) {
        // Update local state
        setEvents(prevEvents => 
          prevEvents.map(event => 
            event._id === eventId 
              ? { 
                  ...event, 
                  isAgentRegistered: false,
                  agentRegistrationStatus: null
                }
              : event
          )
        );
      }
    } catch (err) {
      console.error("Unregistration error:", err);
      alert(err.response?.data?.message || "Failed to unregister from class");
    } finally {
      setProcessingEventId(null);
    }
  };

  // Navigation functions
  const goToPreviousWeek = () => {
    setCurrentWeek(prev => subWeeks(prev, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeek(prev => addWeeks(prev, 1));
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  // Group events by day for display
  const eventsByDay = useMemo(() => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 0 });
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i);
      const dayEvents = events.filter(event => {
        const eventDate = parseISO(event.date);
        return isSameDay(eventDate, day);
      }).sort((a, b) => a.startTime.localeCompare(b.startTime));
      
      days.push({
        date: day,
        events: dayEvents
      });
    }
    
    return days;
  }, [events, currentWeek]);

  // Get location icon based on location type
  const getLocationIcon = (location) => {
    switch (location) {
      case "zoom":
        return <Video className="w-4 h-4" />;
      case "in-person":
        return <MapPin className="w-4 h-4" />;
      case "hybrid":
        return <Users className="w-4 h-4" />;
      default:
        return <Globe className="w-4 h-4" />;
    }
  };

  // Get registration button props
  const getRegistrationButton = (event) => {
    const isProcessing = processingEventId === event._id;
    
    if (event.isAgentRegistered) {
      return {
        onClick: () => handleUnregister(event._id),
        disabled: isProcessing,
        className: "px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-1.5",
        children: isProcessing ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <UserX className="w-4 h-4" />
            <span>Unregister</span>
          </>
        )
      };
    }
    
    return {
      onClick: () => handleRegister(event._id),
      disabled: isProcessing,
      className: "px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-1.5",
      children: isProcessing ? (
        <>
          <Loader className="w-4 h-4 animate-spin" />
          <span>Processing...</span>
        </>
      ) : (
        <>
          <UserCheck className="w-4 h-4" />
          <span>Register</span>
        </>
      )
    };
  };

  // Event Details Modal
  const EventDetailsModal = ({ event, onClose }) => {
    if (!event) return null;

    const buttonProps = getRegistrationButton(event);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-start">
            <h3 className="text-lg font-semibold text-gray-900">Class Details</h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Class Name */}
            <div>
              <h4 className="text-xl font-bold text-gray-900">
                {event.class?.name || event.eventName}
              </h4>
              {event.class?.type && (
                <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                  {event.class.type}
                </span>
              )}
            </div>

            {/* Date and Time */}
            <div className="flex items-center gap-2 text-gray-700">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <span>{format(parseISO(event.date), "EEEE, MMMM d, yyyy")}</span>
            </div>
            
            <div className="flex items-center gap-2 text-gray-700">
              <Clock className="w-5 h-5 text-gray-400" />
              <span>
                {event.startTime}
                {event.endTime && ` - ${event.endTime}`}
              </span>
            </div>

            {/* Location */}
            <div className="flex items-center gap-2 text-gray-700">
              {getLocationIcon(event.location)}
              <span className="capitalize">{event.location.replace("-", " ")}</span>
            </div>

            {/* Instructor */}
            {(event.instructor || event.instructorName) && (
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-5 h-5 text-gray-400" />
                <span>
                  {event.instructor 
                    ? `${event.instructor.firstName} ${event.instructor.lastName}`
                    : event.instructorName}
                </span>
              </div>
            )}

            {/* Description */}
            {event.resources?.description && (
              <div className="border-t pt-4">
                <h5 className="font-semibold text-gray-900 mb-2">Description</h5>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {event.resources.description}
                </p>
              </div>
            )}

            {/* Resources */}
            {(event.resources?.zoomLink || event.resources?.folderLink || event.resources?.marketingLink) && (
              <div className="border-t pt-4">
                <h5 className="font-semibold text-gray-900 mb-2">Resources</h5>
                <div className="space-y-2">
                  {event.resources.zoomLink && (
                    <a
                      href={event.resources.zoomLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <Video className="w-4 h-4" />
                      Join Zoom Meeting
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {event.resources.folderLink && (
                    <a
                      href={event.resources.folderLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Class Materials
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Registration Status */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-900">Registration</span>
                {event.isAgentRegistered && (
                  <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Registered
                  </span>
                )}
              </div>
              
              {/* Registration Button */}
              <button {...buttonProps} className={`w-full ${buttonProps.className}`}>
                {buttonProps.children}
              </button>

              {/* External Registration Notice */}
              {event.isExternalEvent && event.isAgentRegistered && !event.agentExternalRegistrationCompleted && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Please complete external registration to confirm your spot.
                  </p>
                  {event.externalEventDetails?.registrationLink && (
                    <a
                      href={event.externalEventDetails.registrationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm font-medium"
                    >
                      Complete Registration
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // List View Component
  const ListView = () => (
    <div className="space-y-4">
      {eventsByDay.map(({ date, events: dayEvents }) => {
        if (dayEvents.length === 0) return null;
        
        return (
          <div key={date.toISOString()} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h4 className={`font-semibold ${isToday(date) ? "text-blue-600" : "text-gray-900"}`}>
                {format(date, "EEEE, MMM d")}
                {isToday(date) && <span className="ml-2 text-sm text-blue-500">(Today)</span>}
              </h4>
            </div>
            <div className="divide-y divide-gray-200">
              {dayEvents.map(event => {
                const buttonProps = getRegistrationButton(event);
                
                return (
                  <div key={event._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEventDetails(true);
                          }}
                          className="text-left w-full"
                        >
                          <h5 className="font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
                            {event.class?.name || event.eventName}
                          </h5>
                        </button>
                        
                        <div className="mt-1 space-y-1">
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {event.startTime}
                            </span>
                            <span className="flex items-center gap-1">
                              {getLocationIcon(event.location)}
                              <span className="capitalize">{event.location.replace("-", " ")}</span>
                            </span>
                          </div>
                          
                          {(event.instructor || event.instructorName) && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <User className="w-3.5 h-3.5" />
                              {event.instructor 
                                ? `${event.instructor.firstName} ${event.instructor.lastName}`
                                : event.instructorName}
                            </div>
                          )}

                          {event.isAgentRegistered && (
                            <div className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Registered
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <button {...buttonProps}>
                        {buttonProps.children}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {events.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg">
          <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No classes scheduled this week</p>
        </div>
      )}
    </div>
  );

  // Calendar Grid View Component
  const CalendarView = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Days of week header */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
          <div key={day} className="px-2 py-3 text-center text-sm font-semibold text-gray-700 border-r border-gray-200 last:border-r-0">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {eventsByDay.map(({ date, events: dayEvents }) => (
          <div 
            key={date.toISOString()} 
            className={`min-h-[100px] p-2 border-r border-b border-gray-200 last:border-r-0 ${
              isToday(date) ? "bg-blue-50" : ""
            }`}
          >
            <div className={`text-sm font-semibold mb-1 ${isToday(date) ? "text-blue-600" : "text-gray-700"}`}>
              {format(date, "d")}
            </div>
            
            <div className="space-y-1">
              {dayEvents.slice(0, 3).map(event => (
                <button
                  key={event._id}
                  onClick={() => {
                    setSelectedEvent(event);
                    setShowEventDetails(true);
                  }}
                  className={`w-full text-left p-1 rounded text-xs truncate transition-colors ${
                    event.isAgentRegistered 
                      ? "bg-green-100 text-green-800 hover:bg-green-200" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <div className="font-medium truncate">
                    {event.startTime} - {event.class?.name || event.eventName}
                  </div>
                </button>
              ))}
              
              {dayEvents.length > 3 && (
                <button
                  onClick={() => {
                    setViewMode("list");
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  +{dayEvents.length - 3} more
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Week View Component (compact for mobile)
  const WeekView = () => (
    <div className="space-y-3">
      {eventsByDay.filter(({ events }) => events.length > 0).map(({ date, events: dayEvents }) => (
        <div key={date.toISOString()} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className={`font-semibold text-sm mb-2 ${isToday(date) ? "text-blue-600" : "text-gray-900"}`}>
            {format(date, "EEE, MMM d")}
            {isToday(date) && <span className="ml-1 text-xs">(Today)</span>}
          </div>
          
          <div className="space-y-2">
            {dayEvents.map(event => (
              <button
                key={event._id}
                onClick={() => {
                  setSelectedEvent(event);
                  setShowEventDetails(true);
                }}
                className={`w-full text-left p-2 rounded-lg transition-colors ${
                  event.isAgentRegistered 
                    ? "bg-green-50 border border-green-200 hover:bg-green-100" 
                    : "bg-gray-50 border border-gray-200 hover:bg-gray-100"
                }`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {event.class?.name || event.eventName}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                      <span>{event.startTime}</span>
                      <span>•</span>
                      <span className="capitalize">{event.location.replace("-", " ")}</span>
                    </div>
                  </div>
                  {event.isAgentRegistered && (
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {events.length === 0 && !loading && (
        <div className="text-center py-8 bg-white rounded-lg">
          <CalendarIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No classes this week</p>
        </div>
      )}
    </div>
  );

  return (
    <div id="agent-calendar-container" className="space-y-4">
      {/* Header */}
      <div id="calendar-header" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Class Calendar</h2>
            <p className="text-sm text-gray-600 mt-1">
              View and register for upcoming classes
            </p>
          </div>
          
          {/* View Mode Toggle - Hidden on mobile */}
          {!isMobile && (
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === "list" 
                    ? "bg-white text-gray-900 shadow-sm" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === "calendar" 
                    ? "bg-white text-gray-900 shadow-sm" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </button>
              <button
                onClick={() => setViewMode("week")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === "week" 
                    ? "bg-white text-gray-900 shadow-sm" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Monitor className="w-4 h-4" />
                Week
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div id="week-navigation" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="text-center">
            <h3 className="font-semibold text-gray-900">
              {format(startOfWeek(currentWeek, { weekStartsOn: 0 }), "MMM d")} - {format(endOfWeek(currentWeek, { weekStartsOn: 0 }), "MMM d, yyyy")}
            </h3>
            <button
              onClick={goToCurrentWeek}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-1"
            >
              Go to current week
            </button>
          </div>
          
          <button
            onClick={goToNextWeek}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Next week"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div id="calendar-content">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading classes...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
              <p className="text-red-600 font-medium">Error loading classes</p>
              <p className="text-gray-600 text-sm mt-1">{error}</p>
              <button
                onClick={fetchEvents}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            {(isMobile || viewMode === "list") && <ListView />}
            {!isMobile && viewMode === "calendar" && <CalendarView />}
            {!isMobile && viewMode === "week" && <WeekView />}
          </>
        )}
      </div>

      {/* Event Details Modal */}
      {showEventDetails && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => {
            setShowEventDetails(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Legend */}
      <div id="calendar-legend" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-gray-700">Legend:</span>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
            <span className="text-gray-600">Registered</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
            <span className="text-gray-600">Not Registered</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
            <span className="text-gray-600">Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentCalendar;